import { useCallback, useEffect, useRef, useState } from "react";
import { appCache } from "../../../cache/appCache";

type DashboardCacheEntry<T> = {
  fetchedAt: number;
  data: T;
};

const readDashboardCache = <T>(key: string): DashboardCacheEntry<T> | null =>
  appCache.get<DashboardCacheEntry<T>>("dashboard", key) ?? null;

// Serves dashboard data from the cache (appCache bucket "dashboard"); calls `fetcher` only when
// nothing is cached yet (first visit after login) or when `refresh` is called.
export const useCachedDashboard = <T>(key: string, fetcher: () => Promise<T>) => {
  const [entry, setEntry] = useState<DashboardCacheEntry<T> | null>(() => readDashboardCache<T>(key));
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refresh = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      const data = await fetcherRef.current();
      if (data == null) throw new Error("Empty dashboard response");
      const fresh = { fetchedAt: Date.now(), data };
      appCache.set("dashboard", fresh, key);
      setEntry(fresh);
    } catch {
      setError("Failed to load Data");
    } finally {
      setRefreshing(false);
    }
  }, [key]);

  useEffect(() => {
    if (!readDashboardCache(key)) refresh();
  }, [key, refresh]);

  return {
    data: entry?.data,
    fetchedAt: entry?.fetchedAt,
    // true only while there is nothing to show yet
    loading: !entry && !error,
    refreshing,
    error,
    refresh,
  };
};

export const formatLastUpdated = (fetchedAt: number | undefined): string => {
  if (!fetchedAt) return "—";
  const date = new Date(fetchedAt);
  const time = date.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" });
  if (date.toDateString() === new Date().toDateString()) return `Today, ${time}`;
  return `${date.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}, ${time}`;
};
